import { Logger, CubePresenter, ScopeSlot, FlipIntent, NOOP_VOID, Presenter } from 'cube'

import { MainPresenter } from '../main/main.presenter'

import { AnaliseReacoesKey } from './analise-reacoes.key'
import { AnaliseReacoesScope, type SelectOption, ResumoScope, GradeScope, GradeRowScope, GradeRowDetailScope } from './analise-reacoes.scope'

import queryReacoes, { type QueryReacoesRequest } from './service/query-notificacoes'

const LOG = Logger.get('AnaliseReacoesPresenter')

export class AnaliseReacoesPresenter extends CubePresenter<MainPresenter, AnaliseReacoesScope> {
    // :: Fields
    protected parentSlot: ScopeSlot = NOOP_VOID
    protected rowIdGen = 1

    // :: Constructor
    constructor(app: MainPresenter) {
        super(app, new AnaliseReacoesScope())

        const filtroCtrl = new Presenter(this, this.scope.filtro)
        this.scope.filtro.statusBulaOptions = mkStatusBulaOptions()
        this.scope.filtro.gravidadeOptions = mkGravidadeOptions()
        this.scope.filtro.onLimpar = filtroCtrl.action(this.onLimpar, this)
        this.scope.filtro.onConsultar = filtroCtrl.action(this.onConsultar, this)
        this.scope.filtro.onBuscaGeralTextChanged = filtroCtrl.action(this.onBuscaGeralTextChanged, this)
        this.scope.filtro.onBuscaReacaoTextChanged = filtroCtrl.action(this.onBuscaReacaoTextChanged, this)
        this.scope.filtro.onBuscaMedicamentoChanged = filtroCtrl.action(this.onBuscaMedicamentoTextChanged, this)
        this.scope.filtro.onStatusBulaChanged = filtroCtrl.action(this.onStatusBulaChanged, this)
        this.scope.filtro.onGravidadeChanged = filtroCtrl.action(this.onGravidadeChanged, this)
        this.scope.filtro.onPeriodoChange = filtroCtrl.action(this.onPeriodoChanged, this)
        this.scope.filtro.update = filtroCtrl.update
    }

    // :: Cube lifecycle

    override async applyParameters(intent: FlipIntent, initialization: boolean) {
        const key = new AnaliseReacoesKey(this.app, intent)

        this.parentSlot = key.parentSlot

        // Puxa configuracao da intencao
        let changed = this.updatePeriodo(key) || initialization
        changed = this.updateBuscaGeralText(key) || changed
        changed = this.updateBuscaReacaoText(key) || changed
        changed = this.updateBuscaMedicamentoText(key) || changed
        changed = this.updateStatusBulaMedicamentosGeral(key) || changed
        changed = this.updateGravidadeMedicamentosGeral(key) || changed

        this.parentSlot(this.scope)

        if (changed && key.temDados) {
            await this.onConsultar()
        }

        return true
    }

    override publishParameters(intent: FlipIntent): void {
        const key = new AnaliseReacoesKey(this.app, intent)
        key.dataInicial = this.scope.filtro.periodo[0]
        key.dataFinal = this.scope.filtro.periodo[1]
        key.buscaGeralText = this.scope.filtro.buscaGeralText
        key.buscaReacaoText = this.scope.filtro.buscaReacaoText
        key.buscaMedicamentoText = this.scope.filtro.buscaMedicamentoText
        key.statusBula = this.scope.filtro.statusBula
        key.gravidade = this.scope.filtro.gravidade
        key.temDados = this.scope.grade !== null
    }

    // :: Listeners

    async onBuscaGeralTextChanged(txt: string) {
        this.scope.filtro.buscaGeralText = txt
    }

    async onBuscaReacaoTextChanged(txt: string) {
        this.scope.filtro.buscaReacaoText = txt
    }

    async onBuscaMedicamentoTextChanged(txt: string) {
        this.scope.filtro.buscaMedicamentoText = txt
    }

    async onStatusBulaChanged(status: number) {
        this.scope.filtro.statusBula = status
    }

    async onGravidadeChanged(status: number) {
        this.scope.filtro.gravidade = status
    }

    async onPeriodoChanged(periodo: Array<Date>) {
        if (periodo.length === 1) {
            this.scope.filtro.periodo = periodo
        } else if (periodo.length > 1) {
            this.scope.filtro.periodo = periodo
        }
        this.scope.filtro.periodo = periodo
    }

    async onLimpar() {
        this.scope.filtro.buscaGeralText = ''
        this.scope.filtro.buscaMedicamentoText = ''
        this.scope.filtro.buscaReacaoText = ''
        this.scope.filtro.statusBula = 0
        this.scope.filtro.gravidade = 0
        this.scope.resumo = null
        this.scope.grade = null
    }

    async onConsultar() {
        let dataInicial = this.scope.filtro.periodo[0] || new Date()
        let dataFinal = this.scope.filtro.periodo[1] || new Date()

        if (dataInicial.getTime() > dataFinal.getTime()) {
            let tmp = dataFinal
            dataFinal = dataInicial
            dataInicial = tmp
        }

        this.scope.filtro.periodo[0] = dataInicial
        this.scope.filtro.periodo[1] = dataFinal

        const queryRequest: QueryReacoesRequest = {
            dataInicio: dataInicial.toISOString().substring(0, 10),
            dataFim: dataFinal.toISOString().substring(0, 10),
            buscaGeralText: this.scope.filtro.buscaGeralText,
            buscaReacaoText: this.scope.filtro.buscaReacaoText,
            buscaMedicamentoText: this.scope.filtro.buscaMedicamentoText,
            statusBulaValue: this.scope.filtro.statusBula,
            gravidadeValue: this.scope.filtro.gravidade
        }

        if (LOG.isDebugEnabled()) {
            LOG.debug(`queryReacoes(${JSON.stringify(queryRequest)})`)
        }

        this.scope.loading = true
        const data = await queryReacoes(queryRequest).finally(() => this.scope.loading = false)

        data.grade.titulo.qtdeNotificacoes = data.grade.titulo.qtdeNotificacoes
        data.grade.titulo.periodoDeAnalise = data.grade.titulo.periodoDeAnalise

        const gradeCtrl = new Presenter(this, new GradeScope())
        this.scope.grade = gradeCtrl.scope
        this.scope.grade.qtdeNotificacoes = data.grade.titulo.qtdeNotificacoes
        this.scope.grade.periodoDeAnalise = data.grade.titulo.periodoDeAnalise
        for (const rowData of data.grade.linhas) {
            const rowCtrl = new Presenter(this, new GradeRowScope())
            rowCtrl.scope.uid = `${this.rowIdGen++}`
            rowCtrl.scope.data = rowData
            rowCtrl.scope.update = rowCtrl.update
            this.scope.grade.rows.push(rowCtrl.scope)

            for (const rowDetailData of rowData.reacoes) {
                const rowDeailtCtrl = new Presenter(this, new GradeRowDetailScope())
                rowDeailtCtrl.scope.uid = rowDetailData.id
                rowDeailtCtrl.scope.left = rowDetailData.left
                rowDeailtCtrl.scope.right = rowDetailData.right
                rowDeailtCtrl.scope.update = rowDeailtCtrl.update
                rowCtrl.scope.rows.push(rowDeailtCtrl.scope)
            }
        }
        this.scope.grade.update = gradeCtrl.update

        const resumoCtrl = new Presenter(this, new ResumoScope())
        this.scope.resumo = resumoCtrl.scope
        this.scope.resumo.data = data.resumo
        this.scope.resumo.update = resumoCtrl.update
    }

    // :: Update ViewState from Intent

    updatePeriodo(key: AnaliseReacoesKey) {
        const now = new Date()

        const defaultDataFinal = now
        const defaultDataInicial = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)

        const curDataInicial = this.scope.filtro.periodo[0]
        const currentDataFinal = this.scope.filtro.periodo[1]

        let prmDataInicial = key.dataInicial || defaultDataInicial
        let pDataFinal = key.dataFinal || defaultDataFinal

        if (prmDataInicial.getTime() > pDataFinal.getTime()) {
            let tmp = pDataFinal
            pDataFinal = prmDataInicial
            prmDataInicial = tmp
        }

        this.scope.filtro.periodo = [prmDataInicial, pDataFinal]

        let v1 = prmDataInicial ? prmDataInicial.getTime() : 0
        let v2 = curDataInicial ? curDataInicial.getTime() : 0
        if (v1 !== v2) {
            return true
        }

        v1 = pDataFinal ? pDataFinal.getTime() : 0
        v2 = currentDataFinal ? currentDataFinal.getTime() : 0
        return v1 !== v2
    }

    updateBuscaGeralText(key: AnaliseReacoesKey) {
        const ov = this.scope.filtro.buscaGeralText
        const nv = key.buscaGeralText
        this.scope.filtro.buscaGeralText = nv
        return ov !== nv
    }

    updateBuscaReacaoText(key: AnaliseReacoesKey) {
        const ov = this.scope.filtro.buscaReacaoText
        const nv = key.buscaReacaoText
        this.scope.filtro.buscaReacaoText = nv
        return ov !== nv
    }

    updateBuscaMedicamentoText(key: AnaliseReacoesKey) {
        const ov = this.scope.filtro.buscaMedicamentoText
        const nv = key.buscaMedicamentoText
        this.scope.filtro.buscaMedicamentoText = nv
        return ov !== nv
    }

    updateStatusBulaMedicamentosGeral(key: AnaliseReacoesKey) {
        const ov = this.scope.filtro.statusBula
        const nv = key.statusBula
        this.scope.filtro.statusBula = nv
        return ov !== nv
    }

    updateGravidadeMedicamentosGeral(key: AnaliseReacoesKey) {
        const ov = this.scope.filtro.gravidade
        const nv = key.gravidade
        this.scope.filtro.gravidade = nv
        return ov !== nv
    }
}

export default AnaliseReacoesPresenter

// :: Statics

function mkStatusBulaOptions(): SelectOption[] {
    return [
        { value: 0, label: 'Todos os Status' },
        { value: 2, label: 'Fora da Bula' },
        { value: 3, label: 'Não verificado' },
        { value: 4, label: 'Na bula' }
    ]
}

function mkGravidadeOptions(): SelectOption[] {
    return [
        { value: 0, label: 'Todas' },
        { value: 1, label: 'Ameaça à vida' },
        { value: 2, label: 'Anomalia congênita ou malformação ao nascer' },
        { value: 4, label: 'Hospitalização' },
        { value: 8, label: 'Incapacidade persistente ou significativa' },
        { value: 16, label: 'Outro efeito clinicamente significativo' },
        { value: 32, label: 'Resultou em óbito' }
    ]
}
